// Gets all ticket responses for a threadRef
// threadRef meaning ref where the value is the thread model

import { unref } from "vue"
import {
  fetchTicketResponsesByInteractionIds,
  getTicketResponsesByThreadId,
  createTicketResponse as apiCreateTicketResponse,
  updateTicketResponse as apiUpdateTicketResponse,
} from "@/api/ticket_responses"
import { TicketResponseStatusEnum } from "@/utils/constants"

export const useTicketResponses = (thread) => {

    const isLoading = ref(false)
    const ticketResponses = ref([])
    const lastTicketResponse = computed(() => ticketResponses.value.at(0) || {})
    const count = computed(() => ticketResponses.value?.length || 0)

    const draftTicketResponses = ref([])
    const draftCount = computed(() => draftTicketResponses.value?.length || 0)
    const currentDraftResponse = ref(null)

    const getTicketResponses = async (status = TicketResponseStatusEnum.LIVE) => {
        const resolvedThread = unref(thread)
        if(!resolvedThread?._id) {
            ticketResponses.value = []
            return []
        }

        try {
          const data = await getTicketResponsesByThreadId({
            threadId: resolvedThread._id,
          });
          ticketResponses.value = data;
          return data;
        }
        finally {
          isLoading.value = false
        }
    }

    const getDraftTicketResponses = async () => {
        const resolvedThread = unref(thread)
        const interactionId = resolvedThread?.ticket_interaction_id

        if (!interactionId) {
            draftTicketResponses.value = []
            return []
        }

        try {
            const { data } = await fetchTicketResponsesByInteractionIds({
                interaction_ids: [interactionId],
                status: TicketResponseStatusEnum.DRAFT,
            })
            draftTicketResponses.value = data
            return data
        } finally {
            // no-op (keep parity with getTicketResponses)
        }
    }

    const createTicketResponse = async (payload) => {
        try {
            const ticketResponse = await apiCreateTicketResponse(payload)

            if (ticketResponse.status === TicketResponseStatusEnum.DRAFT) {
                draftTicketResponses.value = [ticketResponse, ...draftTicketResponses.value]
                currentDraftResponse.value = ticketResponse
            } else {
                ticketResponses.value = [ticketResponse, ...ticketResponses.value]
            }

            return ticketResponse
        } catch (err) {
            console.error(err)
            throw err
        }
    }

    const updateTicketResponse = async (id, payload) => {
        const resolvedId = id?._id ?? id?.id ?? id
        const draftList = draftTicketResponses.value || []
        const liveList = ticketResponses.value || []

        const draftIdx = draftList.findIndex((tr) => (tr?._id ?? tr?.id) === resolvedId)
        const liveIdx = liveList.findIndex((tr) => (tr?._id ?? tr?.id) === resolvedId)

        const oldBucket = draftIdx !== -1 ? "draft" : liveIdx !== -1 ? "live" : "none"

        try {
            const updated = await apiUpdateTicketResponse(resolvedId, payload)
            const newBucket = updated?.status === TicketResponseStatusEnum.DRAFT ? "draft" : "live"

            // Same bucket -> replace in place (preserve order). If not in memory, prepend.
            if (oldBucket === "draft" && newBucket === "draft") {
                if (draftIdx !== -1) {
                    draftTicketResponses.value[draftIdx] = updated
                } else {
                    draftTicketResponses.value = [updated, ...draftList]
                }
                return updated
            }

            if (oldBucket === "live" && newBucket === "live") {
                if (liveIdx !== -1) {
                    ticketResponses.value[liveIdx] = updated
                } else {
                    ticketResponses.value = [updated, ...liveList]
                }
                return updated
            }

            // Bucket changed (or wasn't in memory): remove from old, prepend to new.
            const nextDraft = oldBucket === "draft" && draftIdx !== -1
                ? draftList.filter((_, i) => i !== draftIdx)
                : draftList
            const nextLive = oldBucket === "live" && liveIdx !== -1
                ? liveList.filter((_, i) => i !== liveIdx)
                : liveList

            if (newBucket === "draft") {
                draftTicketResponses.value = [updated, ...nextDraft]
                ticketResponses.value = nextLive
            } else {
                ticketResponses.value = [updated, ...nextLive]
                draftTicketResponses.value = nextDraft
            }

            return updated
        } catch (err) {
            console.error(err)
            throw err
        }
    }

    const threadDataKey = computed(() => {
        const t = unref(thread)
        if (!t?.interactions?.length) return ''
        const ids = t.interactions.map(i => i.id).sort().join(',')
        return `${ids}|${t.ticket_interaction_id || ''}`
    })

    watch(
        threadDataKey,
        () => {
            getTicketResponses()
            getDraftTicketResponses()
        },
        { immediate: true }
    )

    return {
        ticketResponses,
        lastTicketResponse,
        count,
        draftTicketResponses,
        draftCount,
        currentDraftResponse,
        isLoading,
        getTicketResponses,
        getDraftTicketResponses,
        createTicketResponse,
        updateTicketResponse,
    }
}
